import {
  Outlet,
  createRootRouteWithContext,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { useEffect } from "react";
import TanStackQueryDevtools from "../integrations/tanstack-query/devtools";
import type { QueryClient } from "@tanstack/react-query";
import { canAccess } from "@/lib/perms";
import { userInfoQueryOptions } from "@/lib/queries/auth";
import { Toaster } from "@/components/ui/sonner";
import { showPendingFlashMessage } from "@/lib/utils/flash";

interface MyRouterContext {
  queryClient: QueryClient;
}

const isDev = import.meta.env.VITE_ENVIRONMENT !== "production";

const ROUTES_WITHOUT_USER_PREFETCH = new Set([
  "/auth/login",
  "/auth/signup",
  "/auth/forgot-password",
  "/auth/verify",
  "/auth/verify-code",
  "/auth/guest-signup",
  "/auth/signin",
  "/login",
  "/signin",
  "/pricing",
]);

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: RootComponent,
  async beforeLoad(ctx) {
    const permGuards = ctx.matches
      .flatMap((m) => m.staticData.permissionGuards)
      .filter(Boolean);

    const shouldPrefetchUser = !ROUTES_WITHOUT_USER_PREFETCH.has(
      ctx.location.pathname,
    );

    if (permGuards.length > 0) {
      const userInfo =
        await ctx.context.queryClient.ensureQueryData(userInfoQueryOptions);
      const arePermGuardsValid = permGuards.every(({ resource, actions }) =>
        canAccess(userInfo, resource, actions),
      );
      if (!arePermGuardsValid) {
        if (import.meta.env.DEV) {
          console.warn(
            `Tried to acccess ${ctx.location.pathname} without necessary perms`,
          );
        }
        throw redirect({ to: "/" });
      }
    } else if (shouldPrefetchUser) {
      void ctx.context.queryClient.ensureQueryData(userInfoQueryOptions);
    }
  },
  head({ matches }) {
    const foundMatch = matches.findLast((m) => m.staticData.title);
    let title = "Governance Dashboard";
    if (foundMatch?.staticData.title) {
      title = `${foundMatch.staticData.title} - ` + title;
    }
    return {
      meta: [
        {
          title,
        },
      ],
    };
  },
});

function RootComponent() {
  const problematicScroll = useRouterState({
    select: (s) => s.matches.some((m) => m.staticData.isProblematicScrollPage),
  });
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    showPendingFlashMessage();
  }, [pathname]);

  useEffect(() => {
    if (problematicScroll) document.body.dataset.problematicScroll = "";
    else delete document.body.dataset.problematicScroll;

    return () => void delete document.body.dataset.problematicScroll;
  }, [problematicScroll]);

  return (
    <>
      <Outlet />

      <Toaster />

      <TanStackDevtools
        config={{
          position: "bottom-left",
          hideUntilHover: !isDev,
          requireUrlFlag: !isDev,
          urlFlag: "devtools",
          openHotkey: ["Control", "A"],
        }}
        eventBusConfig={
          import.meta.env.DEV
            ? { port: 3001, debug: true, connectToServerBus: true }
            : undefined
        }
        plugins={[
          { name: "Tanstack Router", render: <TanStackRouterDevtoolsPanel /> },
          TanStackQueryDevtools,
        ]}
      />
    </>
  );
}
